import {ChangeDetectionStrategy, Component, ElementRef, Input, OnInit, ViewChild} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {BaseComponent, StringCustomValidatorContent, StringValidatorType, TemplateFeature,} from 'compob2b-libs';
import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {StringValidator} from '../../../../../../../admin-shared/custom-validators/StringValidator';
import {DamItemTemplateFeaturesService, TemplateFeatureValue} from '../../dam-item-template-features.service';

@Component({
  selector: 'app-tf-string',
  templateUrl: './tf-string.component.html',
  styleUrls: ['./tf-string.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TfStringComponent extends BaseComponent implements OnInit {
  @Input() feature: TemplateFeature;
  @Input() featureLink = false;
  @ViewChild('inputTextArea') inputTextArea: ElementRef;

  private readonly maxTextAreaLength = this.environment.maxTextAreaLength;
  private readonly maxInputLength = this.environment.maxInputLength;

  isTextArea = false;
  public isLongContent = false;
  public trimString = true;

  form = new FormGroup({
    featureValue: new FormControl(null)
  });

  validatorName: string = null;
  typeValidator: StringValidatorType = null;
  customValidator: StringCustomValidatorContent = null;
  valueExample: string = null;

  constructor(protected readonly templateFeatureService: DamItemTemplateFeaturesService) {
    super();
  }

  ngOnInit(): void {
    this.setValidator();

    this.form.get('featureValue').valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.unsubscribe),
      )
      .subscribe(data => {
        this.setFeature(data !== null && data.trim() !== '' ? data.trim() : null, this.form.valid, this.isShowEmptyWarning());
        this.cdr.detectChanges();
      });

    this.setDefaultValue();

    this.templateFeatureService.isFeaturesModelReloaded()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(_ => {
        this.setDefaultValue();
        this.cdr.detectChanges();
      });
  }

  private setValidator(): void {
    if (this.feature.validator !== null) {
      this.validatorName = this.feature.validator.header;
      this.typeValidator = this.feature.validator.content.stringValidator.typeValidator;
      this.valueExample = this.feature.validator.example;
      this.customValidator = this.feature.validator.content.stringValidator.customValidator;

      this.featureValue.addValidators(StringValidator.getValidator(this.feature.validator.content.stringValidator));
    }
  }

  private setDefaultValue(): void {
    const featureModel = this.templateFeatureService.getFeatureModel();

    if (this.feature.id in featureModel) {
      this.featureValue.setValue(featureModel[this.feature.id], {emitEvent: false});
    }

    this.setDefaultFeatureValue(this.featureValue.value);
  }

  private setFeature(featureValue: TemplateFeatureValue, valid: boolean, empty: boolean): void {
    const stringValue = featureValue as string;
    this.isTextArea = stringValue !== null && stringValue.length > this.maxInputLength;
    this.isLongContent = stringValue !== null && stringValue.length > this.maxTextAreaLength;

    this.templateFeatureService.setFeature(
      this.feature.groupId,
      this.feature.id,
      featureValue,
      valid,
      empty
    );
  }

  private setDefaultFeatureValue(featureValue: TemplateFeatureValue): void {
    this.templateFeatureService.setDefaultFeatureValue(
      this.feature.groupId,
      this.feature.id,
      featureValue,
      this.form.valid,
      this.isShowEmptyWarning()
    );
  }

  isEmpty(): boolean {
    return !this.featureValue.value;
  }

  isRequired(): boolean {
    return this.feature.required;
  }

  isShowEmptyWarning(): boolean {
    return this.isRequired() && this.isEmpty();
  }

  get featureValue(): any {
    return this.form.get('featureValue');
  }

  toggleTextarea() {
    this.trimString = !this.trimString;
  }

  resizeOnTextAreaInput(inputEvent: any) {
    this.resizeTextAreaToFitContent(inputEvent.target);
  }

  private resizeTextAreaToFitContent(nativeElement: any): void {
    nativeElement.style.height = '0px';
    nativeElement.style.height = (nativeElement.scrollHeight + 3) + 'px';
  }
}
